import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useInView } from 'framer-motion'
import { useRef } from 'react'
import { 
  Code2, 
  Brain, 
  Globe, 
  Shield, 
  Cloud,
  ArrowRight, 
  CheckCircle,
  Lightbulb,
  Layers,
  Headphones
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

const Services = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })
  const { t } = useTranslation()

  const services = [
    {
      icon: Code2,
      title: t('servicesPage.customSoftware.title'),
      description: t('servicesPage.customSoftware.description'),
      features: [
        t('servicesPage.customSoftware.features.requirements'),
        t('servicesPage.customSoftware.features.development'),
        t('servicesPage.customSoftware.features.integration'),
        t('servicesPage.customSoftware.features.maintenance')
      ],
      gradient: "from-blue-500 to-cyan-500",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200"
    },
    {
      icon: Brain,
      title: t('servicesPage.ai.title'),
      description: t('servicesPage.ai.description'),
      features: [
        t('servicesPage.ai.features.models'),
        t('servicesPage.ai.features.automation'),
        t('servicesPage.ai.features.analytics'),
        t('servicesPage.ai.features.integration')
      ],
      gradient: "from-violet-500 to-purple-500",
      bgColor: "bg-violet-50",
      borderColor: "border-violet-200"
    },
    {
      icon: Globe,
      title: t('servicesPage.web.title'),
      description: t('servicesPage.web.description'),
      features: [
        t('servicesPage.web.features.sites'),
        t('servicesPage.web.features.apps'),
        t('servicesPage.web.features.ux'),
        t('servicesPage.web.features.performance')
      ],
      gradient: "from-cyan-500 to-teal-500",
      bgColor: "bg-cyan-50",
      borderColor: "border-cyan-200"
    },
    {
      icon: Shield,
      title: t('servicesPage.cybersecurity.title'),
      description: t('servicesPage.cybersecurity.description'),
      features: [
        t('servicesPage.cybersecurity.features.audit'),
        t('servicesPage.cybersecurity.features.protection'),
        t('servicesPage.cybersecurity.features.monitoring'),
        t('servicesPage.cybersecurity.features.compliance')
      ],
      gradient: "from-green-500 to-emerald-500",
      bgColor: "bg-green-50",
      borderColor: "border-green-200"
    },
    {
      icon: Cloud,
      title: t('servicesPage.cloud.title'),
      description: t('servicesPage.cloud.description'),
      features: [
        t('servicesPage.cloud.features.migration'),
        t('servicesPage.cloud.features.infrastructure'),
        t('servicesPage.cloud.features.scaling'),
        t('servicesPage.cloud.features.optimization')
      ],
      gradient: "from-indigo-500 to-blue-500",
      bgColor: "bg-indigo-50",
      borderColor: "border-indigo-200"
    }
  ]

  const additionalServices = [
    {
      icon: Lightbulb,
      title: t('servicesPage.consulting.title'),
      description: t('servicesPage.consulting.description')
    },
    {
      icon: Layers,
      title: t('servicesPage.architecture.title'),
      description: t('servicesPage.architecture.description')
    },
    {
      icon: Headphones,
      title: t('servicesPage.support.title'),
      description: t('servicesPage.support.description')
    }
  ]

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary-50 via-white to-accent-50">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('servicesPage.title')} <span className="gradient-text">{t('servicesPage.headline')}</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('servicesPage.description')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Services */}
      <section ref={ref} className="section-padding bg-white">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('servicesPage.main.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('servicesPage.main.description')}
            </p>
          </motion.div>

          <div className="space-y-16">
            {services.map((service, index) => {
              const IconComponent = service.icon
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className={`grid lg:grid-cols-2 gap-12 items-center ${
                    index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                  }`}
                >
                  {/* Content */}
                  <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>

                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                      {service.title}
                    </h3>

                    <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                      {service.description}
                    </p>

                    <ul className="space-y-3 mb-8">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start space-x-3">
                          <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <button className="btn-primary inline-flex items-center">
                      <Link to="/contact">
                      {t('servicesPage.cta')}
                      </Link>
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </button>
                  </div>

                  {/* Visual */}
                  <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                    <div className={`${service.bgColor} rounded-2xl p-8 border ${service.borderColor} relative overflow-hidden`}>
                      <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-r ${service.gradient} opacity-10 rounded-full -translate-y-16 translate-x-16`}></div>
                      <div className={`absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-r ${service.gradient} opacity-10 rounded-full translate-y-12 -translate-x-12`}></div>
                      
                      <div className="relative z-10">
                        <div className={`w-20 h-20 bg-gradient-to-r ${service.gradient} rounded-2xl flex items-center justify-center mb-8`}>
                          <IconComponent className="w-10 h-10 text-white" />
                        </div>

                        <div className="space-y-4">
                          <div className="h-3 bg-white/50 rounded-full overflow-hidden">
                            <div className={`h-full bg-gradient-to-r ${service.gradient} rounded-full w-4/5`}></div>
                          </div>
                          <div className="h-3 bg-white/50 rounded-full overflow-hidden">
                            <div className={`h-full bg-gradient-to-r ${service.gradient} rounded-full w-3/5`}></div>
                          </div>
                          <div className="h-3 bg-white/50 rounded-full overflow-hidden">
                            <div className={`h-full bg-gradient-to-r ${service.gradient} rounded-full w-5/6`}></div>
                          </div>
                        </div>


                      </div>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="section-padding bg-gray-50">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('servicesPage.additional.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('servicesPage.additional.description')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {additionalServices.map((service, index) => {
              const IconComponent = service.icon
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 card-hover text-center"
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {service.description}
                  </p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>
    </div>
  )
}

export default Services
